import { createApp } from 'vue'
import { createHead } from '@vueuse/head'; // 导入 createHead
import ElementPlus from 'element-plus'
import 'element-plus/es/components/message/style/css'
import 'element-plus/es/components/message-box/style/css'

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import fontAwesomeIcons from './utils/fontawesomeIcons';

import App from './App.vue'
import router from './router'
import store from './store'
import i18n, { ensureLocaleLoaded } from './locales'
import { setUnauthorizedHandler } from '@/api/http'
import { clearSessionCache } from '@/api/auth'

import {
    // 模板直接使用
    List, Checked, Failed, Link, Delete, Loading,
    // BaseLogin 通过 <component :is="field.icon"> 动态渲染
    User, Lock, Key
} from '@element-plus/icons-vue'
import 'element-plus/theme-chalk/dark/css-vars.css'
import './styles/global.css'

// OverlayScrollbars 悬浮滚动条
import 'overlayscrollbars/overlayscrollbars.css'


library.add(...fontAwesomeIcons);

const app = createApp(App);
const head = createHead(); // 创建 head 对象

app.component('font-awesome-icon', FontAwesomeIcon);
// Element Plus 图标按需注册（白名单），避免全量引入 ~300KB 图标包
const elementPlusIcons = { List, Checked, Failed, Link, Delete, Loading, User, Lock, Key };
for (const [name, component] of Object.entries(elementPlusIcons)) {
    app.component(name, component);
}

// 全局错误处理：捕获未预期的渲染/生命周期错误，避免静默失败
app.config.errorHandler = (err, instance, info) => {
    console.error('[vue error]', info, err);
};
// 捕获未处理的 Promise 拒绝（Vue 3 渲染错误之外的异步遗漏）
window.addEventListener('unhandledrejection', (event) => {
    console.error('[unhandled rejection]', event.reason);
});

// 401 统一处理：清空会话状态，并按当前路由区域跳转对应登录页
let redirecting401 = false;
setUnauthorizedHandler(() => {
    store.commit('setAuthType', null);
    // 会话已失效，清空会话检查缓存，避免守卫再用旧缓存放行
    clearSessionCache();
    if (redirecting401) return;
    redirecting401 = true;
    const current = router.currentRoute.value;
    const auth = current?.meta?.auth;
    let target = null;
    if (auth === 'admin' && current.name !== 'adminLogin') {
        target = { name: 'adminLogin' };
    } else if (auth === 'user' && current.name !== 'login') {
        target = { name: 'login' };
    }
    const done = () => { redirecting401 = false; };
    if (target) {
        // 静默跳转登录页，不弹出错误提示（路由守卫负责认证 UX）
        router.push(target).finally(done);
    } else {
        done();
    }
});

// 将 dark 类应用到根元素
const applyDarkModeClass = (isDarkMode) => {
    document.documentElement.classList.toggle('dark', isDarkMode);
};

// 根据 useDarkMode 的值初始化暗色模式
const initDarkModeClass = () => {
    let isDarkMode;

    // 判断用户是否是自定义模式
    if (store.state.cusDarkMode && store.state.useDarkMode !== null) {
        // 用户手动设置了暗色模式
        isDarkMode = store.state.useDarkMode;
    } else {
        // 跟随系统模式或时间
        isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (!isDarkMode) {
            const hour = new Date().getHours();
            isDarkMode = hour >= 22 || hour < 6;
        }
        // 更新 useDarkMode 的值
        store.commit('setUseDarkMode', isDarkMode);
    }

    applyDarkModeClass(isDarkMode);
};

// 预设网站标题的函数
const presetSiteTitle = (userConfig) => {
    document.title = userConfig?.siteTitle || 'CloudFree-ImgHub';
};

// 预设和更新网站图标的函数
const presetSiteIcon = (isDarkMode, userConfig) => {
    // 同时更改 icon apple-touch-icon 和 mask-icon
    const existingIcons = document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"]');
    existingIcons.forEach(icon => icon.remove());

    const iconLink = document.createElement('link');
    const appleIconLink = document.createElement('link');
    const maskIconLink = document.createElement('link');
    iconLink.rel = 'icon';
    appleIconLink.rel = 'apple-touch-icon';
    maskIconLink.rel = 'mask-icon';

    if (isDarkMode) {
        iconLink.href = userConfig?.siteIcon || '/pub/static/media/logo-dark.png';
        appleIconLink.href = userConfig?.siteIcon || '/pub/static/media/logo-dark.png';
        maskIconLink.href = userConfig?.siteIcon || '/pub/static/media/logo-dark.png';
    } else {
        iconLink.href = userConfig?.siteIcon || '/pub/static/media/logo.png';
        appleIconLink.href = userConfig?.siteIcon || '/pub/static/media/logo.png';
        maskIconLink.href = userConfig?.siteIcon || '/pub/static/media/logo.png';
    }

    document.head.appendChild(iconLink);
    document.head.appendChild(appleIconLink);
    document.head.appendChild(maskIconLink);
};

// 语言包与用户配置并行加载，两者就绪后再挂载应用
Promise.all([
    store.dispatch('fetchUserConfig'),
    ensureLocaleLoaded()
]).then(() => {
    // 初始化时应用 dark 模式
    initDarkModeClass();

    // 预设网站标题和图标
    presetSiteTitle(store.getters.userConfig);
    presetSiteIcon(store.state.useDarkMode, store.getters.userConfig);

    // 监听 useDarkMode 和 cusDarkMode 的变化
    store.subscribe((mutation, state) => {
        if (mutation.type === 'setUseDarkMode' && store.state.cusDarkMode) {
            applyDarkModeClass(state.useDarkMode);
            // 同时更新网站图标
            presetSiteIcon(state.useDarkMode, store.getters.userConfig);
        }

        // 监听 cusDarkMode 变化，当设置为 false 时重新初始化
        if (mutation.type === 'setCusDarkMode' && !mutation.payload) {
            // 切换到跟随系统模式，重新初始化
            initDarkModeClass();
            // 同时更新网站图标
            presetSiteIcon(store.state.useDarkMode, store.getters.userConfig);
        }
    });

    app.use(store).use(router).use(ElementPlus).use(i18n).use(head).mount('#app');
}).catch(error => {
    console.error('Failed to load user configuration:', error);
    app.use(store).use(router).use(ElementPlus).use(i18n).use(head).mount('#app');
})
